// src/components/GestionDispositivosSesion.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { FaLaptop, FaMobileAlt, FaDesktop, FaTrashAlt, FaShieldAlt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import './GestionDispositivosSesion.css';

const getDeviceIcon = (userAgent = '') => {
    const ua = userAgent.toLowerCase();
    if (ua.includes('android') || ua.includes('iphone') || ua.includes('mobile')) {
        return <FaMobileAlt className="device-type-icon mobile" />;
    }
    if (ua.includes('mac') || ua.includes('windows') || ua.includes('linux')) {
        return <FaLaptop className="device-type-icon laptop" />;
    }
    return <FaDesktop className="device-type-icon desktop" />;
};

const parseBrowserName = (userAgent = '') => {
    const ua = userAgent;
    if (ua.includes('Edg/')) return 'Microsoft Edge';
    if (ua.includes('Chrome/')) return 'Google Chrome';
    if (ua.includes('Firefox/')) return 'Mozilla Firefox';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    if (ua.includes('Android')) return 'Navegador Android / App';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS Safari / App';
    return 'Dispositivo Web / Móvil';
};

const parseOS = (userAgent = '') => {
    const ua = userAgent;
    if (ua.includes('Windows NT 10')) return 'Windows 10/11';
    if (ua.includes('Windows NT')) return 'Windows';
    if (ua.includes('Mac OS X')) return 'macOS';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    if (ua.includes('Linux')) return 'Linux';
    return 'Sistema Operativo';
};

const formatFecha = (timestamp) => {
    if (!timestamp) return 'Reciente';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Reciente';
    return date.toLocaleString('es-MX', {
        dateStyle: 'medium',
        timeStyle: 'short'
    });
};

const GestionDispositivosSesion = () => {
    const { currentUser, logout } = useAuth();
    const [sesiones, setSesiones] = useState([]);
    const [loading, setLoading] = useState(true);
    const localDeviceId = localStorage.getItem('app_device_id');

    useEffect(() => {
        if (!currentUser || currentUser.isOffline) {
            setLoading(false);
            return;
        }

        const sesionesRef = collection(db, 'usuarios', currentUser.uid, 'sesiones_activas');
        const unsubscribe = onSnapshot(sesionesRef, (snapshot) => {
            const list = snapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            }));

            // Ordenar: primero la sesión local actual, luego por último acceso
            list.sort((a, b) => {
                if (a.id === localDeviceId) return -1;
                if (b.id === localDeviceId) return 1;
                const timeA = a.ultimoAcceso?.toDate ? a.ultimoAcceso.toDate().getTime() : 0;
                const timeB = b.ultimoAcceso?.toDate ? b.ultimoAcceso.toDate().getTime() : 0;
                return timeB - timeA;
            });

            setSesiones(list);
            setLoading(false);
        }, (error) => {
            console.warn('GestionDispositivosSesion: Error leyendo sesiones activas', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser, localDeviceId]);

    const handleRevocarSesion = async (sesionId, isCurrent) => {
        if (!currentUser) return;

        const result = await Swal.fire({
            title: isCurrent ? '¿Cerrar tu sesión actual?' : '¿Cerrar sesión remota?',
            text: isCurrent 
                ? 'Se cerrará la sesión en este dispositivo e irás a la pantalla de inicio de sesión.' 
                : 'El dispositivo seleccionado será desconectado inmediatamente.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, cerrar sesión',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'usuarios', currentUser.uid, 'sesiones_activas', sesionId));
                if (isCurrent) {
                    await logout();
                } else {
                    Swal.fire({
                        title: 'Sesión terminada',
                        text: 'La sesión fue revocada exitosamente.',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } catch (err) {
                console.error('Error al revocar sesión:', err);
                Swal.fire('Error', 'No se pudo revocar la sesión. Intenta nuevamente.', 'error');
            }
        }
    };

    if (loading) {
        return (
            <div className="gestion-sesiones-dispositivos-card">
                <div className="sesiones-skeleton-item"></div>
                <div className="sesiones-skeleton-item"></div>
            </div>
        );
    }

    return (
        <div className="gestion-sesiones-dispositivos-card">
            <div className="sesiones-header">
                <div className="sesiones-header-title">
                    <FaShieldAlt className="header-shield-icon" />
                    <div>
                        <h3>Dispositivos y Sesiones Activas</h3>
                        <p>Administra los dispositivos donde tu cuenta se encuentra abierta actualmente.</p>
                    </div>
                </div>
                <div className="sesiones-count-badge" title={`${sesiones.length} ${sesiones.length === 1 ? 'dispositivo activo' : 'dispositivos activos'}`}>
                    {sesiones.length}
                </div>
            </div>

            <div className="sesiones-list">
                {sesiones.length === 0 ? (
                    <div className="sesiones-empty">
                        <FaExclamationTriangle className="empty-icon" />
                        <p>No se encontraron registros de sesiones activas.</p>
                    </div>
                ) : (
                    sesiones.map((sesion) => {
                        const isCurrentDevice = sesion.id === localDeviceId;
                        const browserName = parseBrowserName(sesion.userAgent);
                        const osName = parseOS(sesion.userAgent);

                        return (
                            <div 
                                key={sesion.id} 
                                className={`sesion-item-card ${isCurrentDevice ? 'current-device' : ''}`}
                            >
                                <div className="sesion-item-left">
                                    {getDeviceIcon(sesion.userAgent)}
                                    <div className="sesion-item-details">
                                        <div className="sesion-title-row">
                                            <h4>{browserName} en {osName}</h4>
                                            {isCurrentDevice && (
                                                <span className="current-badge">
                                                    <FaCheckCircle /> Este dispositivo
                                                </span>
                                            )}
                                        </div>
                                        <p className="sesion-meta">
                                            <span>Último acceso: {formatFecha(sesion.ultimoAcceso)}</span>
                                        </p>
                                    </div>
                                </div>

                                <div className="sesion-item-actions">
                                    <button
                                        type="button"
                                        className="btn-revocar-sesion"
                                        onClick={() => handleRevocarSesion(sesion.id, isCurrentDevice)}
                                        title="Cerrar sesión en este dispositivo"
                                    >
                                        <FaTrashAlt />
                                        <span>Cerrar sesión</span>
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default GestionDispositivosSesion;
